import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import moment from 'moment';
import { FinancialService } from '../financial.service';
import { LoginService } from '../login.service';
import { SettingsService } from '../settings.service';
import { LanguageService } from '../language.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-financial-report',
  templateUrl: './financial-report.component.html',
  styleUrls: ['./financial-report.component.scss'],
  standalone: false
})
export class FinancialReportComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  pageDirection = 'ltr';
  filterFromDate: Date | null = new Date();
  filterToDate: Date | null = new Date();
  doctorFilter: number | 'ALL' = 'ALL';
  examTypeFilter = 'ALL';
  payerFilter = 'ALL';
  patientFilter = '';
  payerStatusFilter = 'ALL';
  performerStatusFilter = 'ALL';
  groupBy = 'none';

  examTypeOptions: Array<{ value: string; label: string }> = [];
  rows: any[] = [];
  groups: any[] = [];
  totals: any = null;
  totalRows = 0;
  pageSize = 50;
  pageIndex = 0;
  loading = false;
  exporting = false;
  error = false;
  selected = new Set<number>();
  bulkTarget: 'payer' | 'performer' = 'payer';
  bulkStatus = 'paid';

  constructor(
    public financialService: FinancialService,
    public loginService: LoginService,
    private settingsService: SettingsService,
    private languageService: LanguageService,
    private translate: TranslateService
  ) { }

  private examTypeLabel(examType: string): string {
    const key = 'appointments.gui.exams.' + examType;
    const translated = this.translate.instant(key);
    return translated === key ? examType : translated;
  }

  async ngOnInit() {
    this.pageDirection = this.languageService.getPageDirection();
    const exams = await this.settingsService.getExams();
    if (Array.isArray(exams)) {
      const options = new Map<string, string>();
      exams
        .filter((exam) => !exam.custom && !this.financialService.combinedExamTypes.includes(exam.examType))
        .forEach((exam) => {
          const isClinic = exam.examType === 'Clinics' && exam.ClinicTemplateID;
          const value = isClinic ? `Clinics:${exam.ClinicTemplateID}` : exam.examType;
          if (!options.has(value)) options.set(value, isClinic ? exam.examName : this.examTypeLabel(exam.examType));
        });
      this.examTypeOptions = Array.from(options, ([value, label]) => ({ value, label }));
    }
  }

  private filters() {
    return {
      from: moment(this.filterFromDate).format('YYYY-MM-DD'),
      to: moment(this.filterToDate).format('YYYY-MM-DD'),
      UID: this.doctorFilter,
      examType: this.examTypeFilter,
      payer: this.payerFilter,
      patient: this.patientFilter.trim(),
      payerStatus: this.payerStatusFilter,
      performerStatus: this.performerStatusFilter,
      groupBy: this.groupBy
    };
  }

  get canSearch(): boolean {
    return !!this.filterFromDate && !!this.filterToDate && !this.loading;
  }

  async search(resetPage = true, silent = false) {
    if (!this.canSearch) return;
    if (resetPage) {
      this.pageIndex = 0;
      if (this.paginator) this.paginator.pageIndex = 0;
    }
    if (!silent) this.loading = true;
    this.error = false;
    try {
      const result = await this.financialService.getReport(
        this.filters(),
        this.pageSize,
        this.pageIndex * this.pageSize
      );
      this.rows = result.rows.map((row) => ({ ...row, selected: this.selected.has(row.ExamID) }));
      this.groups = result.groups;
      this.totals = result.totals;
      this.totalRows = result.totalRows;
    } catch (error) {
      console.error('Error loading financial report', error);
      this.error = true;
    } finally {
      this.loading = false;
    }
  }

  onPage(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.search(false);
  }

  async changeStatus(row: any, field: 'payerStatus' | 'performerStatus') {
    try {
      const result = await this.financialService.setStatus({ ExamID: row.ExamID, [field]: row[field] });
      row.payerStatus = result.payerStatus;
      row.performerStatus = result.performerStatus;
      await this.search(false, true);
    } catch (error) {
      console.error('Error saving status', error);
      this.error = true;
      await this.search(false, true);
    }
  }

  toggleRow(row: any) {
    if (row.selected) {
      this.selected.add(row.ExamID);
    } else {
      this.selected.delete(row.ExamID);
    }
  }

  get allSelected(): boolean {
    return this.rows.length > 0 && this.rows.every((row) => row.selected);
  }

  toggleAll(checked: boolean) {
    this.rows.forEach((row) => {
      row.selected = checked;
      this.toggleRow(row);
    });
  }

  async applyBulk() {
    if (!this.selected.size) return;
    const body: any = { ExamIDs: Array.from(this.selected) };
    body[this.bulkTarget === 'payer' ? 'payerStatus' : 'performerStatus'] = this.bulkStatus;
    try {
      await this.financialService.setStatusBulk(body);
      this.selected.clear();
      await this.search(false, true);
    } catch (error) {
      console.error('Error saving bulk status', error);
      this.error = true;
    }
  }

  async exportExcel() {
    if (!this.canSearch) return;
    this.exporting = true;
    try {
      const blob = await this.financialService.exportReport(this.filters());
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `financial-report-${moment(this.filterFromDate).format('YYYY-MM-DD')}-${moment(this.filterToDate).format('YYYY-MM-DD')}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting financial report', error);
      this.error = true;
    } finally {
      this.exporting = false;
    }
  }
}
